<template>
    <div class="random-call-session">
        <div
            v-if="otherUser"
            class="d-flex align-items-center justify-content-between p-2 bg-dark text-white"
        >
            <div class="d-flex align-items-center">
                <img
                    v-if="otherUser.photo"
                    :src="otherUser.photo"
                    class="rounded mr-2"
                    style="width: 32px; height: 32px; object-fit: cover"
                />
                <div>
                    <div class="small">{{ otherUser.name }}</div>
                    <div class="small text-secondary">{{ otherUser.age }} · {{ otherUser.country }}</div>
                </div>
            </div>
            <div class="text-right">
                <div v-if="!streamReady" class="small text-info">
                    <font-awesome-icon icon="fa-duotone fa-spinner fa-spin" />
                    {{ waitingLabel }}
                </div>
                <div v-else-if="freePreviewActive" class="small text-warning">
                    <font-awesome-icon icon="fa-duotone fa-gift" />
                    {{ previewSecondsLeft }}s {{ $t("free preview") }}
                </div>
                <div v-else class="small">
                    <font-awesome-icon icon="fa-duotone fa-clock" />
                    {{ minutesRemaining }} {{ $t("min remaining") }}
                </div>
            </div>
        </div>

        <b-alert
            v-if="!streamReady && !onboardingUser.creator"
            variant="primary"
            show
            class="mb-0"
        >
            <div class="small text-center">
                {{ $t("You will not be charged until their video stream is active") }}
            </div>
        </b-alert>

        <b-alert
            v-if="!isCameraOn && onboardingUser.creator"
            variant="warning"
            show
            class="mb-0"
        >
            <div class="small text-center">
                {{ $t("You will not earn any credits until your video stream is active") }}
            </div>
        </b-alert>

        <random-call-agora
            v-if="currentRandomCall && currentRandomCall.uuid"
            :channel="currentRandomCall.uuid"
            @hangup="hangup"
            @skip="skip"
        />
    </div>
</template>

<script>
import { mapState } from "vuex";

import RandomCallAgora from "@/components/randomCall/RandomCallAgora";

export default {
    components: { RandomCallAgora },
    data() {
        return {
            previewSecondsLeft: 15,
            previewTicker: null,
            billingInterval: null,
            startBillingTimeout: null,
            lastBillingTime: null,
        };
    },
    computed: {
        ...mapState("randomCall", [
            "currentRandomCall",
            "matchedFromUser",
            "matchedToUser",
            "freePreviewSeconds",
            "minutesRemaining",
            "hasRemoteVideoTrack",
            "isCameraOn",
        ]),
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
        otherUser() {
            if (!this.matchedFromUser || !this.matchedToUser) return null;
            return this.matchedFromUser.id === this.onboardingUser.id
                ? this.matchedToUser
                : this.matchedFromUser;
        },
        // mirrors call/CallDetails — customer waits on remote video, creator on local camera.
        // free-preview countdown and billing are gated on this; until it's true, neither runs.
        streamReady() {
            return this.onboardingUser.creator
                ? this.isCameraOn
                : this.hasRemoteVideoTrack;
        },
        freePreviewActive() {
            return this.previewSecondsLeft > 0;
        },
        waitingLabel() {
            return this.onboardingUser.creator
                ? this.$t("Waiting for your camera")
                : this.$t("Waiting for video stream");
        },
    },
    watch: {
        // start the timers the moment the stream becomes ready, NOT on mount.
        // this is the same gate CallDetails uses for the existing private call
        // billing UX.
        streamReady(ready) {
            if (ready) {
                this.startSession();
            }
        },
    },
    mounted() {
        if (this.streamReady) {
            this.startSession();
        }
    },
    beforeDestroy() {
        this.stopSession();
    },
    methods: {
        startSession() {
            if (this.previewTicker || this.billingInterval || this.startBillingTimeout) {
                return;
            }

            this.previewSecondsLeft = this.freePreviewSeconds;
            this.previewTicker = setInterval(() => {
                if (this.previewSecondsLeft > 0) {
                    this.previewSecondsLeft--;
                }
            }, 1000);

            if (!this.onboardingUser.creator) {
                this.startBillingTimeout = setTimeout(() => {
                    this.startBilling();
                }, this.freePreviewSeconds * 1000);
            }
        },
        stopSession() {
            if (this.previewTicker) {
                clearInterval(this.previewTicker);
                this.previewTicker = null;
            }
            if (this.startBillingTimeout) {
                clearTimeout(this.startBillingTimeout);
                this.startBillingTimeout = null;
            }
            if (this.billingInterval) {
                clearInterval(this.billingInterval);
                this.billingInterval = null;
            }
        },
        startBilling() {
            this.$store.dispatch("randomCall/charge");
            this.lastBillingTime = performance.now();

            this.billingInterval = setInterval(() => {
                const now = performance.now();
                if (now - this.lastBillingTime >= 60000) {
                    this.$store.dispatch("randomCall/charge");
                    this.lastBillingTime = performance.now();
                }
            }, 1000);
        },
        hangup() {
            this.$store.dispatch("randomCall/hangup");
        },
        skip() {
            this.$store.dispatch("randomCall/skip");
        },
    },
};
</script>

<style scoped>
.random-call-session {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: #000;
}
</style>
